import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Share2, Check, Clock, Calendar, User, ClipboardList, Star, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import OFFERS_DETAIL from '../data/offersDetail';
import EconomieCheckoutModal from '../components/EconomieCheckoutModal';
import QuoteRequestModal from '../components/QuoteRequestModal';
import ContractSignModal from '../components/ContractSignModal';
import { DA, SunOrnament, AwningCorner, PalmShadow, MimosaBranch, BrandMark, BrandFooter, SabrilDecorFrame, SabrilDivider } from '../theme/sabril-da';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Config per offer (matches images) ──────────────────────────────────
const OFFER_META = {
  economie: {
    pill: '48H · EXPRESS',
    HeaderIcon: Clock,
    subtitleKicker: 'L\'ESSENTIEL POUR BIEN DÉMARRER',
    priceLabel: 'Investissement',
    priceValue: '2 000€',
    priceMeta: 'TTC · MA 2 190€ · FR·ES 2 790€',
    forWho: [
      'Restaurateur qui veut un diagnostic sans engagement avant de bouger',
      'Trouver où tu perds 8 000€/mois sans le savoir',
      'Un plan d\'action clair en 48h, prêt à exécuter seul',
    ],
    format: '48h sur place + 30j de suivi WhatsApp',
    included: [
      'Audit rentabilité complet 360°',
      'Menu & offre repositionnés',
      'Fiches de poste + food cost',
      'Livraison PDF sur-mesure',
    ],
    ctaPrimaryLabel: 'RÉSERVER DÈS 2 000€',
    ctaPrimaryIcon: '✈️',
    hasContract: true,
  },
  affaires: {
    pill: '90J · DISTANCIEL',
    HeaderIcon: Calendar,
    subtitleKicker: 'STRATÉGIE & RENTABILITÉ',
    priceLabel: 'Investissement',
    priceValue: 'sur mesure',
    priceMeta: 'DEVIS 24H · PAS D\'ABONNEMENT',
    forWho: [
      'Resto actif qui veut structurer sa rentabilité sans arrêter la machine',
      'Refonte marge, menu et acquisition — 90 jours de pilotage rapproché',
      'Un bras droit stratégique à distance, WhatsApp prioritaire',
    ],
    format: '100% distanciel · 90 jours',
    included: [
      '90 jours de pilotage',
      '2 RDV visio par mois',
      'WhatsApp prioritaire',
    ],
    ctaPrimaryLabel: 'DEMANDER MON DEVIS',
    ctaPrimaryIcon: '📩',
    hasContract: true,
  },
  premiere: {
    pill: '360° · SUR-MESURE',
    HeaderIcon: Star,
    subtitleKicker: 'ACCOMPAGNEMENT PREMIUM COMPLET',
    priceLabel: 'Investissement',
    priceValue: 'sur mesure',
    priceMeta: 'CAFÉ DE CALIBRAGE OFFERT · PAR COOPTATION',
    forWho: [
      'Dirigeant qui veut un accompagnement 360° premium',
      'Refonte complète marque + rentabilité + équipe',
      'Café de calibrage 45min avant tout engagement',
    ],
    format: '3 à 12 mois · sur-mesure',
    included: [
      'Direction stratégique complète',
      'Refonte marque & positionnement',
      'Coaching dirigeant hebdo',
      'Accès Salon Privé',
    ],
    ctaPrimaryLabel: 'CAFÉ DE CALIBRAGE',
    ctaPrimaryIcon: '📞',
    hasContract: false,
  },
  ebook: {
    pill: '790€ · CONSULTATION PRIVÉE',
    HeaderIcon: FileText,
    subtitleKicker: '90 MINUTES AVEC SABRIL · GUIDE ULTIME OFFERT',
    priceLabel: 'Investissement',
    priceValue: '790€',
    priceMeta: 'TTC · Paiement sécurisé Stripe',
    forWho: [
      'Dirigeants qui veulent un diagnostic direct avec Sabril',
      '2 visios de 45 min · état des lieux + plan d\'action 30 jours',
      'Le Guide Ultime PDF · 75 pages offert en complément',
    ],
    format: '2 visios de 45 min + PDF 75 pages · valable 90 jours',
    included: [
      'Visio #1 · Diagnostic 45 min de votre restaurant',
      'Visio #2 · Plan d\'action 45 min personnalisé',
      'Le Guide Ultime PDF · 75 pages offert',
      '100% déductible du Salon Privé sous 90 jours',
    ],
    ctaPrimaryLabel: 'RÉSERVER À 790€',
    ctaPrimaryIcon: '📅',
    hasContract: false,
  },
  salon: {
    pill: 'INNER CIRCLE',
    HeaderIcon: Star,
    subtitleKicker: 'SALON PRIVÉ · ACCÈS COOPTÉ',
    priceLabel: 'Accès',
    priceValue: 'cooptation',
    priceMeta: 'Sur invitation uniquement',
    forWho: [
      'Dirigeants du réseau Sabril déjà accompagnés',
      'Accès inner circle · masterminds · deals privés',
      'Networking premium restauration',
    ],
    format: 'Communauté privée · à vie',
    included: [
      'Masterminds mensuels',
      'Deals privés & partenariats',
      'Accès direct Sabril',
    ],
    ctaPrimaryLabel: 'DISCUTER AVEC SABRIL',
    ctaPrimaryIcon: '💬',
    hasContract: false,
  },
};

/**
 * OfferDetailPage — deep-linkable, shareable detail page per offer.
 * URL : /offre/:key
 * DA : Sabril · Audit resto — ivory + deep green + gold, editorial Mediterranean.
 */
export default function OfferDetailPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const offer = OFFERS_DETAIL[key];
  const meta = OFFER_META[key];
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!offer) return;
    document.title = `${offer.title.replace(/\.$/, '')} · Sabril`;
    window.scrollTo(0, 0);
  }, [offer]);

  if (!offer || !meta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" style={{ background: DA.ivory }}>
        <h1 className="text-2xl font-black mb-3" style={{ color: DA.greenDeep, fontFamily: DA.serif }}>Offre introuvable</h1>
        <p style={{ color: DA.inkMuted }} className="mb-6">Le lien que tu as suivi n&apos;existe plus.</p>
        <Link to="/prsnl" className="px-4 py-2.5 text-white rounded-full font-bold text-sm" style={{ background: DA.greenDeep }}>
          ← Voir toutes les offres
        </Link>
      </div>
    );
  }

  const { HeaderIcon } = meta;

  const handleCta = async () => {
    if (offer.ctaAction === 'ebook-checkout') {
      try {
        const res = await fetch(`${API_URL}/api/ebook/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ origin_url: window.location.origin, source: 'offre-detail-ebook' }),
        });
        const data = await res.json();
        if (data && data.url) window.location.href = data.url;
      } catch (_e) { /* silent */ }
      return;
    }
    if (offer.ctaAction === 'economie-checkout') { window.dispatchEvent(new CustomEvent('economie:open')); return; }
    if (offer.ctaAction === 'affaires-quote')    { window.dispatchEvent(new CustomEvent('affaires:open')); return; }
    if (offer.ctaAction === 'premiere-quote')    { window.dispatchEvent(new CustomEvent('premiere:open')); return; }
    if (offer.ctaAction === 'open-chat')         { navigate('/prsnl#chat'); return; }
  };

  const share = async () => {
    const shareData = { title: offer.title, text: offer.tagline, url: window.location.href };
    if (navigator.share) { try { await navigator.share(shareData); } catch (_e) { /* cancelled */ } }
    else if (navigator.clipboard) {
      await navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papier');
    }
  };

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: DA.ivory, paddingBottom: 40 }} data-testid={`offer-detail-${offer.key}`}>

      {/* Background decorations — page-wide */}
      <SabrilDecorFrame awningTL awningBR mimosa palm awningSize={140} mimosaSize={130} />

      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 px-4 py-3 flex items-center justify-between"
           style={{ background: `${DA.ivory}EE`, backdropFilter: 'blur(14px)', borderBottom: `0.5px solid ${DA.goldSoft}` }}>
        <button
          type="button"
          onClick={() => navigate('/prsnl#offres')}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full active:scale-95 transition"
          style={{ background: DA.ivoryLight, color: DA.greenDeep, border: `0.5px solid ${DA.goldSoft}` }}
          data-testid="offer-detail-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
          <span className="text-xs font-bold tracking-wide">Toutes les offres</span>
        </button>
        <button
          type="button"
          onClick={share}
          className="p-2 rounded-full active:scale-95 transition"
          style={{ background: DA.ivoryLight, color: DA.greenDeep, border: `0.5px solid ${DA.goldSoft}` }}
          aria-label="Partager"
          data-testid="offer-detail-share"
        >
          <Share2 className="w-3.5 h-3.5" strokeWidth={2.5} />
        </button>
      </div>

      {/* MAIN CARD */}
      <div className="relative mx-4 mt-5 rounded-[24px] overflow-hidden"
           style={{
             background: `linear-gradient(180deg, ${DA.ivoryLight} 0%, ${DA.ivory} 100%)`,
             boxShadow: '0 30px 60px -30px rgba(11,43,36,0.15), 0 0 0 0.5px rgba(216,148,64,0.18)',
             padding: '22px 22px 28px',
           }}
      >
        {/* Top pill + circle icon */}
        <div className="flex items-start justify-between mb-4">
          <span
            style={{
              display: 'inline-block',
              padding: '7px 15px',
              borderRadius: 999,
              border: `1px solid ${DA.goldMain}`,
              background: 'transparent',
              fontFamily: DA.sans,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.16em',
              color: DA.goldMain,
              textTransform: 'uppercase',
            }}
            data-testid="offer-pill"
          >
            {meta.pill}
          </span>
          <span
            className="inline-flex items-center justify-center"
            style={{
              width: 42, height: 42,
              borderRadius: '50%',
              border: `1px solid ${DA.goldMain}`,
              background: 'transparent',
              color: DA.goldMain,
            }}
          >
            <HeaderIcon className="w-4 h-4" strokeWidth={1.8} />
          </span>
        </div>

        {/* Brand mark */}
        <div className="mt-1 mb-2">
          <BrandMark size="md" center />
        </div>

        <div className="flex justify-center mt-4 mb-3">
          <SunOrnament size={30} color={DA.goldMain} />
        </div>

        {/* Main title */}
        <h1 className="text-center" style={{
          fontFamily: DA.serif,
          fontSize: 40,
          fontWeight: 700,
          color: DA.greenDeep,
          letterSpacing: '-0.02em',
          lineHeight: 1.02,
          marginTop: 4,
        }}>
          {offer.key === 'economie' ? 'Classe Économie'
            : offer.key === 'affaires' ? 'Classe Affaires'
            : offer.key === 'premiere' ? 'Première Classe'
            : offer.key === 'ebook' ? 'Sabril Express'
            : 'Salon Privé'}
        </h1>

        <p className="text-center mt-3" style={{
          fontFamily: DA.sans,
          fontSize: 11,
          letterSpacing: '0.32em',
          color: DA.greenDeep,
          fontWeight: 600,
          textTransform: 'uppercase',
          lineHeight: 1.35,
        }}>
          {meta.subtitleKicker}
        </p>

        <div className="flex justify-center mt-5 mb-1">
          <SunOrnament size={26} color={DA.goldMain} opacity={0.9} />
        </div>

        {/* Price block */}
        <div className="text-center mt-4">
          <p style={{
            fontFamily: DA.sans,
            fontSize: 11,
            letterSpacing: '0.32em',
            color: DA.greenDeep,
            fontWeight: 700,
            textTransform: 'uppercase',
          }}>
            {meta.priceLabel}
          </p>
          {meta.priceValue !== 'sur mesure' && meta.priceValue !== 'cooptation' ? (
            <>
              <p style={{ fontFamily: DA.sans, fontSize: 12.5, color: DA.ink, marginTop: 8, letterSpacing: '0.02em' }}>
                À partir de
              </p>
              <p style={{
                fontFamily: DA.serif,
                fontSize: 58,
                fontWeight: 700,
                color: DA.goldMain,
                letterSpacing: '-0.02em',
                lineHeight: 1,
                marginTop: 2,
              }}>
                {meta.priceValue}
              </p>
            </>
          ) : (
            <p style={{
              fontFamily: DA.serif,
              fontStyle: 'italic',
              fontSize: 52,
              fontWeight: 700,
              color: DA.goldMain,
              letterSpacing: '-0.02em',
              lineHeight: 1,
              marginTop: 14,
            }}>
              {meta.priceValue}
            </p>
          )}
          <div className="mt-4 mx-auto rounded-lg"
               style={{
                 background: DA.ivoryDeep,
                 padding: '9px 16px',
                 display: 'inline-block',
                 border: `0.5px solid ${DA.goldSoft}`,
               }}>
            <p style={{
              fontFamily: DA.sans,
              fontSize: 10.5,
              color: DA.ink,
              letterSpacing: '0.14em',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}>
              {meta.priceMeta}
            </p>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 0.5, background: DA.goldSoft, margin: '28px 0 22px' }} />

        {/* POUR QUI */}
        <SectionRow icon={<User className="w-5 h-5" strokeWidth={1.6} />} title="POUR QUI">
          <ul className="space-y-3 mt-1">
            {meta.forWho.map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <span
                  className="inline-flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    width: 20, height: 20,
                    borderRadius: '50%',
                    border: `1.5px solid ${DA.goldMain}`,
                  }}
                >
                  <Check className="w-3 h-3" strokeWidth={2.6} style={{ color: DA.goldMain }} />
                </span>
                <span style={{ fontFamily: DA.sans, fontSize: 14, color: DA.ink, lineHeight: 1.42 }}>
                  {line}
                </span>
              </li>
            ))}
          </ul>
        </SectionRow>

        <div style={{ height: 0.5, background: DA.goldSoft, margin: '22px 0' }} />

        {/* FORMAT */}
        <SectionRow icon={<ClipboardList className="w-5 h-5" strokeWidth={1.6} />} title="FORMAT">
          <p style={{ fontFamily: DA.sans, fontSize: 14, color: DA.ink, marginTop: 5 }}>
            {meta.format}
          </p>
        </SectionRow>

        <div style={{ height: 0.5, background: DA.goldSoft, margin: '22px 0' }} />

        {/* INCLUS */}
        <SectionRow icon={<Star className="w-5 h-5" strokeWidth={1.6} />} title="INCLUS">
          <ul className="space-y-2.5 mt-1">
            {meta.included.map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <span
                  className="inline-flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    width: 18, height: 18,
                    borderRadius: '50%',
                    border: `1.5px solid ${DA.goldMain}`,
                  }}
                >
                  <Check className="w-2.5 h-2.5" strokeWidth={2.8} style={{ color: DA.goldMain }} />
                </span>
                <span style={{ fontFamily: DA.sans, fontSize: 14, color: DA.ink, lineHeight: 1.4 }}>
                  {line}
                </span>
              </li>
            ))}
          </ul>
        </SectionRow>

        {/* CTAs */}
        <div className="mt-8 flex flex-col gap-2.5">
          <button
            type="button"
            onClick={handleCta}
            className="w-full flex items-center justify-center gap-2.5 active:scale-[0.98] transition"
            style={{
              background: DA.greenDeep,
              color: '#FFFFFF',
              padding: '15px 20px',
              borderRadius: 10,
              fontFamily: DA.sans,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              boxShadow: `0 10px 24px -8px ${DA.greenDeep}88`,
            }}
            data-testid={`offer-detail-cta-${offer.key}`}
          >
            <span style={{ color: DA.goldMain, fontSize: 14 }}>{meta.ctaPrimaryIcon}</span>
            {meta.ctaPrimaryLabel}
          </button>

          {meta.hasContract && (
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('contract:open', { detail: { offer: offer.key } }))}
              className="w-full flex items-center justify-center gap-2.5 active:scale-[0.98] transition"
              style={{
                background: 'transparent',
                color: DA.greenDeep,
                padding: '13px 20px',
                borderRadius: 10,
                border: `1.5px solid ${DA.goldMain}`,
                fontFamily: DA.sans,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
              }}
              data-testid={`offer-detail-sign-${offer.key}`}
            >
              <span style={{ color: DA.goldMain, fontSize: 14 }}>✎</span>
              Signer mon contrat
            </button>
          )}
        </div>

        {/* Footer signature */}
        <div className="mt-9">
          <BrandFooter />
        </div>
      </div>

      {/* Detailed content toggle — keeps original content available */}
      {offer.sections && offer.sections.length > 0 && (
        <div className="mx-4 mt-8">
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            className="w-full flex items-center justify-center gap-2 active:scale-[0.98] transition"
            style={{
              background: 'transparent',
              color: DA.greenDeep,
              padding: '12px 18px',
              borderRadius: 999,
              border: `1px solid ${DA.goldSoft}`,
              fontFamily: DA.sans,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
            }}
            data-testid="offer-detail-toggle-more"
          >
            {showMore ? 'Masquer le détail complet' : 'Voir le détail complet'}
            {showMore ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {showMore && (
            <div className="mt-6 space-y-7">
              {offer.sections.map((section) => (
                <div key={section.title}>
                  <p className="px-2 mb-1 text-[10.5px] font-black uppercase tracking-[0.28em]"
                     style={{ color: DA.goldMain }}>
                    {section.title}
                  </p>
                  {section.subtitle && (
                    <p className="px-2 mb-3 italic" style={{ color: DA.inkMuted, fontSize: 12 }}>
                      {section.subtitle}
                    </p>
                  )}
                  <div className="rounded-[14px] overflow-hidden"
                       style={{
                         background: DA.ivoryLight,
                         border: `0.5px solid ${DA.goldSoft}`,
                       }}>
                    {section.items.map((item, idx) => (
                      <div key={item.title} className="flex items-start gap-3 px-4 py-3.5"
                           style={idx < section.items.length - 1 ? { borderBottom: `0.5px solid ${DA.goldSoft}` } : {}}>
                        <span
                          className="inline-flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{
                            width: 22, height: 22,
                            borderRadius: '50%',
                            border: `1.5px solid ${DA.goldMain}`,
                          }}
                        >
                          <Check className="w-3 h-3" strokeWidth={2.6} style={{ color: DA.goldMain }} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p style={{ fontFamily: DA.sans, fontSize: 13.5, fontWeight: 700, color: DA.greenDeep, lineHeight: 1.25 }}>
                            {item.title}
                          </p>
                          {item.sub && (
                            <p style={{ fontFamily: DA.sans, fontSize: 12, color: DA.ink, opacity: 0.75, marginTop: 3, lineHeight: 1.4 }}>
                              {item.sub}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mount modals so CTA can open them without navigating away */}
      <EconomieCheckoutModal />
      <QuoteRequestModal />
      <ContractSignModal />
    </div>
  );
}

// ─── Section row helper (icon + kicker + content) ───────────────────────
function SectionRow({ icon, title, children }) {
  return (
    <div className="flex items-start gap-4">
      <span
        className="inline-flex items-center justify-center flex-shrink-0"
        style={{
          width: 38, height: 38,
          color: DA.goldMain,
          marginTop: 1,
        }}
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p style={{
          fontFamily: DA.sans,
          fontSize: 11.5,
          fontWeight: 800,
          letterSpacing: '0.28em',
          color: DA.goldMain,
          textTransform: 'uppercase',
        }}>
          {title}
        </p>
        {children}
      </div>
    </div>
  );
}
